import React from 'react';
import PropTypes from 'prop-types';
import { List, Modal, Tag } from 'antd';
import {
  errorTypeLabel,
  recordTypeLabel
} from 'views/OptimiserStatus/config';

// Pull the list[Violation] out of whatever shape a blocked write surfaces. The
// write-block backend returns HTTP 400 with a body that is a plain list of
// { record_type, record_id, record_label, error_type, detail }. Depending on where this is
// caught the violations may arrive as the raw array, on error.response.data, or
// under a `violations`/`data` key, so handle each defensively.
export const getViolationsFromError = (error) => {
  if (!error) {
    return [];
  }
  if (Array.isArray(error)) {
    return error;
  }
  if (error.response && Array.isArray(error.response.data)) {
    return error.response.data;
  }
  if (Array.isArray(error.violations)) {
    return error.violations;
  }
  if (Array.isArray(error.data)) {
    return error.data;
  }
  return [];
};

// A single violation row. Prefer the human-readable record label the backend now
// sends (user email / activity name); fall back to the raw id only when it is
// absent. Mirrors the readiness drill-down table (record_label || record_id).
const renderViolation = (violation) => {
  const recordName = violation.record_label || `#${violation.record_id}`;
  return (
    <List.Item key={`${violation.record_type}-${violation.record_id}-${violation.error_type}`}>
      <List.Item.Meta
        title={`${recordTypeLabel(violation.record_type)}: ${recordName}`}
        description={violation.detail}
      />
      <Tag>{errorTypeLabel(violation.error_type)}</Tag>
    </List.Item>
  );
};

// A blocking dialog that lists every contract violation from a rejected write and
// offers a single acknowledge button (FR-5.1, R5.2). Cancel is hidden and the
// dialog is not dismissable by mask/escape so the editor must read and ack it.
const ValidationBlockDialog = ({
  visible, violations, title, okText, onOk
}) => (
  <Modal
    className="bhp-validation-block-dialog"
    title={title}
    visible={visible}
    okText={okText}
    onOk={onOk}
    onCancel={onOk}
    closable={false}
    maskClosable={false}
    keyboard={false}
    cancelButtonProps={{ style: { display: 'none' } }}
  >
    <p>
      This change was blocked because it would leave the following records with missing
      or invalid data the Optimiser requires. Fix each record, then try again.
    </p>
    <List
      size="small"
      dataSource={violations}
      renderItem={renderViolation}
      locale={{ emptyText: 'No violations' }}
    />
  </Modal>
);

ValidationBlockDialog.propTypes = {
  visible: PropTypes.bool,
  violations: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  okText: PropTypes.string,
  onOk: PropTypes.func
};

ValidationBlockDialog.defaultProps = {
  visible: false,
  violations: [],
  title: 'Record cannot be saved',
  okText: 'Acknowledge',
  onOk() {}
};

export default ValidationBlockDialog;
